// What happens when an event is revealed from the current events deck. Each effect changes the game state
// and returns a line for the journal. Events without an entry here are only announced.
//
// Strongest / weakest / most: ties go in favour of the player earliest in turn order, starting with the one whose
// turn it is (they count as the stronger, and are not the weaker). "The two strongest / weakest" means one in a
// 2-player game.
// Choices players make are decisions (see Decisions.js), each player's in turn order from the current player; one with
// a single possible answer is made at once.
// Territories start a colonization auction (see Colonization.js).
const EventNames = require('../Constants/Names/EventNames');
const { TechnologyNames } = require('../Constants/Constants');
const { MilitaryCardTypes } = require('../Constants/Names/CardTypes');
const { AgeOrder, Age } = require('../Constants/Constants');
const TechnologyTypes = require('../Constants/TechnologyTypes');
const { GetCorruption, GetConsumption } = require('../Utilities/Utilities');
const { FireTriggers, TriggerEvents, ProducedBy, Situations, SituationalStrength } = require('../Effects/Effects');
const { RequestDecision, builtCards, SettleBlueBank, AskAgreementTake } = require('./Decisions');

const MilitaryTypes = Object.values(TechnologyTypes.Military);
const UrbanTypes = Object.values(TechnologyTypes.Urban);
const SpecialTypes = Object.values(TechnologyTypes.Special);
const { Farm, Mine } = TechnologyTypes.Production;
const Level = (card) => Math.max(0, AgeOrder.indexOf(card.age));

// Every player, starting with the one whose turn it is; or only the one an event applies to (Development of Planning)
// (Players who resigned are out of the game)
const inTurnOrder = (gameState) => gameState.eventScope ?? gameState.playerStates
    .map((_, offset) => gameState.playerStates[(gameState.currentPlayer + offset) % gameState.playerCount])
    .filter(player => !player.resigned);
const inGame = (gameState) => gameState.playerStates.filter(player => !player.resigned);

const forEachPlayer = (gameState, effect) => inTurnOrder(gameState).forEach(effect);

// Players from the highest value down (ties: earlier in turn order first), or from the lowest up (ties: later in turn
// order first, so the earlier player is not the lowest)
// note: what is compared, noted for everyone to check (see NoteRanking)
const ranked = (gameState, value, highestFirst, note) => {
    if (note)
    {
        NoteRanking(gameState, note, value);
    }
    return inTurnOrder(gameState)
        .map((player, index) => ({ player, index, value: value(player) }))
        .sort((a, b) => highestFirst ? (b.value - a.value || a.index - b.index) : (a.value - b.value || b.index - a.index))
        .map(entry => entry.player);
};

// The rankings an event worked with, so everyone can check who it picked: while an event is resolved (see
// ResolveEvent), each measure compared, once: { measure, label, kind (its icon), entries: [{ player, value, extras }] },
// in the order compared: highest first, or for the weakest lowest first (ties as the event breaks them). extras: what
// cards add only for this ([{ source, value }], e.g. Nostradamus +3 when events pick the weakest), included in the value.
// note: { measure, label, kind, lowestFirst, extras(player) }
const NoteRanking = (gameState, note, value) => {
    const notes = gameState.eventRankings;
    if (!notes || notes.some(other => other.measure === note.measure))
    {
        return;
    }
    notes.push({
        measure: note.measure,
        label: note.label,
        kind: note.kind,
        entries: ranked(gameState, value, !note.lowestFirst).map(player => {
            const extras = note.extras?.(player) ?? [];
            return { player: player.name, value: value(player), ...(extras.length ? { extras } : {}) };
        }),
    });
};
const strength = (player) => player.GetMilitaryStrength();
const StrongestNote = { measure: 'strongest', label: 'Strongest', kind: 'strength' };
const strongest = (gameState, count = 1) => ranked(gameState, strength, true, StrongestNote).slice(0, Math.min(count, inGame(gameState).length - 1));
// (an event picking the weakest counts strength cards add only then, e.g. Nostradamus's +3)
const weakestStrength = (player) => SituationalStrength(player, Situations.EventWeakest);
const weakestExtras = (player) => player.situationalStrengthDelegate.filter(d => d.situation === Situations.EventWeakest)
    .map(d => ({ source: d.Source?.name, value: d.Action() })).filter(extra => extra.value !== 0);
const weakest = (gameState, count = 1) => {
    const note = { measure: 'weakest', label: 'Weakest', kind: 'strength', lowestFirst: true, extras: weakestExtras };
    return ranked(gameState, weakestStrength, false, note).slice(0, Math.min(count, inGame(gameState).length - 1));
};
// Everyone tied for the highest value
const allWithMost = (gameState, value, note) => {
    if (note)
    {
        NoteRanking(gameState, note, value);
    }
    const best = Math.max(...inGame(gameState).map(value));
    return inTurnOrder(gameState).filter(player => value(player) === best);
};
const CultureNote = { measure: 'culture', label: 'Culture', kind: 'culture' };
const names = (players) => players.map(player => player.name).join(" and ");

// --- Small effects, shared by several events
const loseCulture = (player, amount) => { player.culture = Math.max(0, player.culture - amount); };
// (as any free population increase: what cards do then, e.g. Maria Theresa, happens)
const gainPopulation = (player) => require('./Colonization').IncreasePopulationFree(player.gameState, player, 1);
// An unused worker goes back to the yellow bank; without one, the player chooses a worker from one of their cards
const losePopulation = (gameState, player, event) => {
    if (player.workerPool > 0)
    {
        player.workerPool--;
        return;
    }
    RequestDecision(gameState, {
        player, type: 'loseWorker', event,
        prompt: "You have no unused worker: choose the card to take a worker from",
        options: builtCards(player).map(tech => ({ label: tech.name, value: tech.name })),
    });
};

// Asks each of these players, in turn order, for the same kind of decision; options(player) gives each one's answers
const askEach = (gameState, players, spec, options) => players.forEach(player =>
    RequestDecision(gameState, { ...spec, player, options: options(player) }));

// Ways to split a total between food and resources, e.g. 3 -> 3 food, 2 food 1 resource, ...; limit(food, resources)
// can rule some out (e.g. having too little to lose)
const splits = (total, limit = () => true) => Array.from({ length: total + 1 }, (_, food) => ({ food, resources: total - food }))
    .filter(split => limit(split.food, split.resources))
    .map(split => ({
        label: [split.food ? `${split.food} food` : null, split.resources ? `${split.resources} resources` : null].filter(p => p).join(', '),
        value: split,
    }));

// Development of Civilization: 1 food, 1 resource or 1 science less
const CivilizationDiscounts = [
    { source: EventNames.DevelopmentOfCivilization, kind: 'populationFood', amount: 1 },
    { source: EventNames.DevelopmentOfCivilization, kind: 'buildPayment', amount: 1 },
    { source: EventNames.DevelopmentOfCivilization, kind: 'developScience', amount: 1 },
];

// In the event's own choices its discount goes without saying; others (e.g. Masonry) are still noted
const ownDiscountLeftOut = (costNote) => (costNote ?? '').split(', ')
    .filter(part => part && part.replace(/^[+−]\d+ /, '') !== EventNames.DevelopmentOfCivilization).join(', ') || undefined;

// Development of Civilization's choices, as buttons like the usual actions with the discount applied: increase
// population, build a farm, mine or urban building (not a military unit), or develop a technology (a government
// peacefully, or by a revolution: 1 science less, but all civil actions); or nothing. Only what the player could do now is
// offered (a revolution greyed out, with the reason, once a civil action has been spent).
const civilizationOptions = (gameState, player) => {
    const { ActionCategories } = require('./ActionCategories');
    const MoveDefinitions = require('./Moves');
    const { WithCardDiscount, GetGovernmentCost, AdjustmentText } = require('./Costs');
    return WithCardDiscount(player, CivilizationDiscounts, () => {
        const legal = (move) => MoveDefinitions[move.type].validate(gameState, { ...move, player: player.playerNumber, free: true }) === null;
        const from = (id, section) => ActionCategories.find(category => category.id === id).generate(gameState, player)
            .filter(option => legal(option.move))
            .map(option => ({ label: option.label, cost: option.cost, costNote: ownDiscountLeftOut(option.costNote), section, value: option.move }));
        // A government peacefully, or by a revolution (1 science less too, but all civil actions: offered greyed out, with
        // the reason, once one has been spent)
        const { IsShownDisabled } = require('./Costs');
        const militaryRevolution = player.ruleExceptionDelegate.some(rule => rule.rule === 'revolutionWithMilitaryActions');
        const problem = (move) => MoveDefinitions[move.type].validate(gameState, { ...move, player: player.playerNumber, free: true });
        const governments = player.civilHand
            .filter(card => card.Technology?.type === TechnologyTypes.Government)
            .flatMap(card => [false, true].map(revolution => {
                const cost = GetGovernmentCost(player, card.Technology, revolution);
                const value = { type: 'ChangeGovernment', cardId: card.id, revolution };
                const error = problem(value);
                return error && !IsShownDisabled(error) ? null : {
                    label: revolution ? `${card.name} (revolution)` : card.name,
                    cost: revolution ? `${cost.total} science, all ${militaryRevolution ? 'MA' : 'CA'}` : `${cost.total} science`,
                    costNote: ownDiscountLeftOut(AdjustmentText(cost)), section: "Develop", value,
                    ...(error ? { disabled: true, reason: error } : {}),
                };
            }))
            .filter(option => option);
        return [
            ...from('increasePopulation', "Increase Population"),
            ...from('build', "Build"),
            ...from('developTechnology', "Develop"),
            ...governments,
            { label: "Do nothing", value: null },
        ];
    });
};

// Build for free with an unused worker on the player's card of this name, if they can (Development of Religion / Warfare)
const freeBuildOptions = (player, techName) => {
    const tech = player.GetTechnology(techName);
    const urbanFull = tech && UrbanTypes.includes(tech.type)
        && player.technologies.filter(t => t.type === tech.type).reduce((sum, t) => sum + t.GetWorkerCount(), 0) >= player.GetUrbanBuildingLimit();
    if (!tech || player.workerPool < 1 || urbanFull)
    {
        return [];
    }
    return [{ label: `Build ${techName} for free`, value: true }, { label: "Don't build", value: false }];
};
// A blue token goes back to the box: from the blue bank, or else from a farm or mine of the player's choice
const loseBlueToken = (gameState, player, source) => {
    player.ChangeTokens('blue', -1, source);
    SettleBlueBank(gameState, player, source);
};
const drawMilitary = (gameState, player, count) => gameState.DrawMilitaryCards(player, count);
// Workers on the player's cards of these types, and the sum of their levels
const workersOn = (player, types) => player.technologies.filter(tech => types.includes(tech.type)).reduce((sum, tech) => sum + tech.GetWorkerCount(), 0);
const levelsOf = (player, types) => player.technologies.filter(tech => types.includes(tech.type)).reduce((sum, tech) => sum + Level(tech) * tech.GetWorkerCount(), 0);
const allWorkers = (player) => player.workerPool + player.technologies.reduce((sum, tech) => sum + tech.GetWorkerCount(), 0);

// Impact events that score by rank: 4 players 15/10/5/0, 3 players 14/7/0, 2 players 10/0. Ties go to the player who
// played earlier (the one who played last loses them).
const RankScores = { 2: [10, 0], 3: [14, 7, 0], 4: [15, 10, 5, 0] };
// The icon for what an impact compares
const KindOf = { strength: 'strength', 'culture production': 'culture', 'science production': 'science' };
const scoreByRank = (gameState, value, what) => {
    const scores = RankScores[gameState.playerCount] ?? RankScores[4];
    // (ties: the earlier in turn order from the current player takes the better rank, as for strongest and weakest)
    const order = ranked(gameState, value, true, { measure: what, label: what[0].toUpperCase() + what.slice(1), kind: KindOf[what] });
    order.forEach((player, index) => { player.culture += scores[index] ?? 0; });
    return `Civilizations score culture by ${what}: ${order.map((player, index) => `${player.name} ${scores[index] ?? 0}`).join(", ")}.`;
};
// Every civilization scores culture worked out from its own board
const eachScores = (gameState, culture, text) => {
    forEachPlayer(gameState, player => { player.culture += Math.max(0, culture(player)); });
    return text;
};

const EventEffects = {
    // ---------------- Age A
    [EventNames.DevelopmentOfAgriculture]: (gameState) => {
        forEachPlayer(gameState, player => player.GainFood(2));
        return "Each civilization gains 2 food.";
    },
    [EventNames.DevelopmentOfCrafts]: (gameState) => {
        forEachPlayer(gameState, player => player.GainResources(2));
        return "Each civilization gains 2 resources.";
    },
    [EventNames.DevelopmentOfPolitics]: (gameState) => {
        forEachPlayer(gameState, player => drawMilitary(gameState, player, 3));
        return "Each player draws 3 military cards.";
    },
    [EventNames.DevelopmentOfScience]: (gameState) => {
        forEachPlayer(gameState, player => { player.science += 2; });
        return "Each civilization scores 2 science.";
    },
    [EventNames.DevelopmentOfSettlement]: (gameState) => {
        forEachPlayer(gameState, gainPopulation);
        return "Each civilization gains 1 population.";
    },
    [EventNames.DevelopmentOfTradeRoutes]: (gameState) => {
        forEachPlayer(gameState, player => {
            player.science += 1;
            player.GainResources(1);
            player.GainFood(1);
        });
        return "Each civilization scores 1 science and gains 1 resource and 1 food.";
    },

    [EventNames.DevelopmentOfCivilization]: (gameState) => {
        askEach(gameState, inTurnOrder(gameState),
            { type: 'innerAction', event: EventNames.DevelopmentOfCivilization, refresh: 'civilization',
                prompt: "Increase your population, build a farm, mine or urban building, or develop a technology: 1 food, 1 resource or 1 science less",
                context: { discounts: CivilizationDiscounts } },
            player => civilizationOptions(gameState, player));
        return "Each civilization may increase its population, build a farm, mine or urban building, or develop a technology, for 1 food, 1 resource or 1 science less.";
    },
    [EventNames.DevelopmentOfPlanning]: (gameState) => {
        // The server draws 3 of the Age A events not used this game (never Development of Planning itself)
        const drawn = gameState.eventBox.filter(card => card.name !== EventNames.DevelopmentOfPlanning).slice(0, 3);
        gameState.eventBox = gameState.eventBox.filter(card => !drawn.includes(card));
        askEach(gameState, inTurnOrder(gameState),
            { type: 'planning', event: EventNames.DevelopmentOfPlanning, prompt: "Choose one of these events to apply to your civilization alone" },
            () => drawn.map(card => ({ label: card.name, value: card.name, text: card.text, age: card.age })));
        return `Drawn from the box: ${drawn.map(card => require('../Utilities/CardRefs').CardRef(card)).join(' ')}. Each player applies one to their own civilization.`;
    },
    [EventNames.DevelopmentOfMarkets]: (gameState) => {
        askEach(gameState, inTurnOrder(gameState),
            { type: 'gainOne', event: EventNames.DevelopmentOfMarkets, prompt: "Gain 2 food or 2 resources", context: { amount: 2 } },
            () => [{ label: "2 food", value: 'food' }, { label: "2 resources", value: 'resources' }]);
        return "Each civilization gains 2 food or 2 resources.";
    },
    [EventNames.DevelopmentOfReligion]: (gameState) => {
        askEach(gameState, inTurnOrder(gameState),
            { type: 'freeBuild', event: EventNames.DevelopmentOfReligion, prompt: "Build an Age A temple for free?", context: { techName: TechnologyNames.Urban.Religion } },
            player => freeBuildOptions(player, TechnologyNames.Urban.Religion));
        return "Each civilization with an unused worker may build an Age A temple for free.";
    },
    [EventNames.DevelopmentOfWarfare]: (gameState) => {
        askEach(gameState, inTurnOrder(gameState),
            { type: 'freeBuild', event: EventNames.DevelopmentOfWarfare, prompt: "Build a Warrior unit for free?", context: { techName: TechnologyNames.Military.Warrior } },
            player => freeBuildOptions(player, TechnologyNames.Military.Warrior));
        return "Each civilization with an unused worker may build a Warrior unit for free.";
    },

    // ---------------- Age I
    [EventNames.BorderConflict]: (gameState) => {
        const [strong] = strongest(gameState);
        const [weak] = weakest(gameState);
        strong.GainResources(3);
        // The weakest chooses what to destroy (the turn waits: an arena gives strength)
        RequestDecision(gameState, {
            player: weak, type: 'destroy', event: EventNames.BorderConflict, ui: 'boardPick',
            prompt: "Destroy one of your urban buildings, farms or mines: pick it on your board",
            context: { pickLabel: 'Destroy' },
            options: builtCards(weak, [...UrbanTypes, Farm, Mine]).map(tech => ({ label: tech.name, value: tech.name })),
        });
        return `${weak.name} must destroy an urban building, farm or mine; ${strong.name} gains 3 resources.`;
    },
    [EventNames.Foray]: (gameState) => {
        const players = strongest(gameState, 2);
        askEach(gameState, players,
            { type: 'split', event: EventNames.Foray, prompt: "Gain a total of 3 food and/or resources" },
            () => splits(3));
        return `${names(players)} each gain a total of 3 food and/or resources.`;
    },
    [EventNames.Raiders]: (gameState) => {
        const players = weakest(gameState, 2);
        // A split the player cannot pay in full is offered only when nothing better is possible
        askEach(gameState, players,
            { type: 'split', event: EventNames.Raiders, prompt: "Lose a total of 2 food and/or resources", context: { lose: true } },
            player => {
                const total = Math.min(2, player.GetFood() + player.GetResources());
                return splits(total, (food, resources) => food <= player.GetFood() && resources <= player.GetResources());
            });
        return `${names(players)} each lose a total of 2 food and/or resources.`;
    },
    [EventNames.Barbarians]: (gameState) => {
        const [mostCulture] = ranked(gameState, player => player.culture, true, CultureNote);
        if (!weakest(gameState, 2).includes(mostCulture))
        {
            return `${mostCulture.name} has the most culture but is not one of the two weakest: no effect.`;
        }
        losePopulation(gameState, mostCulture, EventNames.Barbarians);
        return `${mostCulture.name}, with the most culture and among the two weakest, loses 1 population.`;
    },
    [EventNames.CallToArms]: (gameState) => {
        const [weakestOne, ...others] = weakest(gameState, 2);
        // Lost actions count against the civilization's next (or current) action phase; 1 military card per action lost
        const lose = (player, count) => {
            const lost = Math.max(0, Math.min(count, player.GetCivilActionCount() - player.civilActionsUsed));
            player.civilActionsUsed += lost;
            drawMilitary(gameState, player, lost);
        };
        lose(weakestOne, 2);
        others.forEach(player => lose(player, 1));
        return `${weakestOne.name} loses 2 civil actions${others.length ? ` and ${names(others)} loses 1` : ''}, drawing a military card for each.`;
    },
    [EventNames.Crusades]: (gameState) => {
        const [strong] = strongest(gameState);
        const [weak] = weakest(gameState);
        strong.culture += 4;
        loseCulture(weak, 4);
        return `${strong.name} scores 4 culture; ${weak.name} loses 4 culture.`;
    },
    [EventNames.CulturalInfluence]: (gameState) =>
        eachScores(gameState, player => player.GetCultureProduction(), "Each civilization scores the culture it produces."),
    [EventNames.DarkAges]: (gameState) => {
        forEachPlayer(gameState, player => {
            if (player.science > 4)
            {
                player.science -= Math.ceil((player.science - 4) / 2);
            }
        });
        return "Each civilization with more than 4 science loses half of the surplus (rounded up).";
    },
    [EventNames.GoodHarvest]: (gameState) => {
        forEachPlayer(gameState, player => player.ProduceFood());
        return "Each civilization produces food (no corruption or consumption).";
    },
    [EventNames.Immigration]: (gameState) => {
        const happiest = allWithMost(gameState, player => player.GetHappyFaces(), { measure: 'happy faces', label: 'Happy faces', kind: 'happy faces' });
        happiest.forEach(gainPopulation);
        return `${names(happiest)}, with the most happy faces, gain${happiest.length === 1 ? 's' : ''} 1 population.`;
    },
    [EventNames.KnowledgeOfTheAncients]: (gameState) => {
        forEachPlayer(gameState, player => {
            player.science += player.wonders.reduce((sum, wonder) => sum + (Level(wonder) === 0 ? 2 : 1), 0);
            if (player.wonderInProgress)
            {
                player.GainResources(Level(player.wonderInProgress.card) === 0 ? 2 : 1);
            }
        });
        return "Each civilization scores 1 science per completed wonder and gains 1 resource for an unfinished one (double for Age A wonders).";
    },
    [EventNames.NewDeposits]: (gameState) => {
        forEachPlayer(gameState, player => player.ProduceResources());
        return "Each civilization produces resources (no corruption).";
    },
    [EventNames.Pestilence]: (gameState) => {
        forEachPlayer(gameState, player => losePopulation(gameState, player, EventNames.Pestilence));
        return "Each civilization loses 1 population.";
    },
    [EventNames.Rats]: (gameState) => {
        forEachPlayer(gameState, player => player.LoseFood(player.GetFood()));
        return "Each civilization loses all its stored food.";
    },
    [EventNames.Rebellion]: (gameState) => {
        forEachPlayer(gameState, player => { player.civilActionsUsed += 2 * player.GetDiscontentWorkers(); });
        return "Each civilization spends 2 civil actions per discontent worker.";
    },
    [EventNames.ReignOfTerror]: (gameState) => {
        const [weak] = weakest(gameState);
        losePopulation(gameState, weak, EventNames.ReignOfTerror);
        return `${weak.name}, the weakest, loses 1 population.`;
    },
    [EventNames.ScientificBreakthrough]: (gameState) => {
        forEachPlayer(gameState, player => { player.science += player.GetScienceProduction(); });
        return "Each civilization scores the science it produces.";
    },
    [EventNames.UncertainBorders]: (gameState) => {
        const [strong] = strongest(gameState);
        const [weak] = weakest(gameState);
        if (weak.yellowBank < 1)
        {
            return `${weak.name}, the weakest, has no yellow token to take.`;
        }
        weak.ChangeTokens('yellow', -1, EventNames.UncertainBorders);
        strong.ChangeTokens('yellow', 1, EventNames.UncertainBorders);
        return `${strong.name} takes 1 yellow token from ${weak.name}.`;
    },

    // ---------------- Age II
    [EventNames.ArmsIndustry]: (gameState) => {
        const unitTypes = [TechnologyTypes.Military.Infantry, TechnologyTypes.Military.Cavalry, TechnologyTypes.Military.Artillery];
        // The level of each civilization's best technology of each unit type (-1 without one)
        const best = (player, type) => Math.max(-1, ...player.technologies.filter(tech => tech.type === type).map(Level));
        const gains = new Map(inGame(gameState).map(player => [player, 0]));
        unitTypes.forEach(type => {
            const top = Math.max(...inGame(gameState).map(player => best(player, type)));
            if (top >= 0)
            {
                inGame(gameState).filter(player => best(player, type) === top).forEach(player => gains.set(player, gains.get(player) + 2));
            }
        });
        gains.forEach((amount, player) => player.GainResources(amount));
        return "Each civilization gains 2 resources for each unit type in which it has the best (or tied best) technology.";
    },
    [EventNames.CivilUnrest]: (gameState) => {
        const discontent = (player) => player.GetDiscontentWorkers();
        if (inGame(gameState).every(player => discontent(player) === 0))
        {
            return "No civilization has discontent workers: no effect.";
        }
        const most = allWithMost(gameState, discontent, { measure: 'discontent workers', label: 'Discontent workers' });
        forEachPlayer(gameState, player => loseCulture(player, 4 * discontent(player)));
        most.forEach(player => loseBlueToken(gameState, player, EventNames.CivilUnrest));
        return `Each civilization loses 4 culture per discontent worker; ${names(most)}, with the most, return${most.length === 1 ? 's' : ''} 1 blue token to the box.`;
    },
    [EventNames.ColdWar]: (gameState) => {
        const players = strongest(gameState, 2);
        players.forEach(player => { player.science += 6; });
        return `${names(players)} score${players.length === 1 ? 's' : ''} 6 science.`;
    },
    [EventNames.CrimeWave]: (gameState) => {
        const players = weakest(gameState, 2);
        players.forEach(player => {
            loseCulture(player, 3);
            loseBlueToken(gameState, player, EventNames.CrimeWave);
        });
        return `${names(players)} lose${players.length === 1 ? 's' : ''} 3 culture and return${players.length === 1 ? 's' : ''} 1 blue token to the box.`;
    },
    [EventNames.EconomicProgress]: (gameState) => {
        forEachPlayer(gameState, player => {
            player.LoseFood(player.LoseResources(GetCorruption(player.blueBank)));
            player.ProduceFood();
            player.LoseFood(GetConsumption(player.yellowBank));
            player.ProduceResources();
        });
        return "Each civilization produces: corruption, food, consumption, then resources.";
    },
    [EventNames.Emigration]: (gameState) => {
        forEachPlayer(gameState, player => {
            const leaving = Math.ceil(player.GetDiscontentWorkers() / 2);
            for (let i = 0; i < leaving; i++)
            {
                losePopulation(gameState, player, EventNames.Emigration);
            }
        });
        return "Each civilization loses half of its discontent workers (rounded up).";
    },
    [EventNames.FreedomOfMovement]: (gameState) => {
        askEach(gameState, inTurnOrder(gameState).filter(player => builtCards(player).length > 0),
            { type: 'moveWorker', event: EventNames.FreedomOfMovement, ui: 'moveWorker',
                prompt: "You may move one worker from one technology card to another, paying the difference if it costs more" },
            () => null);
        return "Each civilization may move one worker from one technology card to another.";
    },
    [EventNames.InternationalAgreement]: (gameState) => {
        const [strong] = strongest(gameState);
        // Taking cards changes the card row, so the turn waits for the strongest. They take cards straight away (the first
        // one gives up their next political phase), or decline
        AskAgreementTake(gameState, strong, 5);
        return `${strong.name}, the strongest, may take up to 5 CA worth of cards by giving up its next political phase.`;
    },
    [EventNames.PoliticsOfStrength]: (gameState) => {
        const [strong] = strongest(gameState);
        const [weak] = weakest(gameState);
        // In the game-ending age, culture instead
        if (gameState.age === Age.Age4)
        {
            strong.culture += 5;
            loseCulture(weak, 3);
            return `${strong.name} scores 5 culture; ${weak.name} loses 3 culture.`;
        }
        drawMilitary(gameState, strong, 5);
        if (weak.militaryHand.length <= 3)
        {
            gameState.militaryDiscard.push(...weak.militaryHand);
            weak.militaryHand = [];
        } else {
            // Which cards are kept could change whether someone attacks the weakest, so the turn waits for it
            RequestDecision(gameState, {
                player: weak, type: 'discardMilitary', event: EventNames.PoliticsOfStrength, blocking: true, ui: 'chooseCards',
                prompt: "Discard 3 of your military cards", options: null, context: { count: 3 },
            });
        }
        return `${strong.name} draws 5 military cards; ${weak.name} discards 3.`;
    },
    [EventNames.IndependenceDeclaration]: (gameState) => {
        const [weak] = weakest(gameState);
        if (weak.colonies.length === 0)
        {
            return `${weak.name}, the weakest, has no colonies to lose: no effect.`;
        }
        // The colony leaves the game; with a choice of colonies, the others wait for it (it changes the board)
        const lost = RequestDecision(gameState, {
            player: weak, type: 'loseColony', event: EventNames.IndependenceDeclaration, blocking: true,
            prompt: "Choose the colony to lose",
            options: weak.colonies.map(colony => ({ label: colony.name, value: colony.id })),
        });
        return lost ?? `${weak.name}, the weakest, loses one of its colonies.`;
    },
    [EventNames.InternationalNegotiations]: (gameState) => {
        const revealer = gameState.GetCurrentPlayerState();
        const [strong] = strongest(gameState);
        const [weak] = weakest(gameState);
        // The one who revealed it chooses; it changes two boards, so the turn waits for the choice
        const options = [['food', weak.GetFood()], ['resources', weak.GetResources()], ['science', weak.science]]
            .filter(([, amount]) => amount >= 3)
            .map(([what]) => ({ label: `3 ${what}`, value: what }));
        RequestDecision(gameState, {
            player: revealer, type: 'negotiate', event: EventNames.InternationalNegotiations, blocking: true,
            prompt: `${strong.name} (the strongest) takes 3 of something from ${weak.name} (the weakest): choose what`,
            options, context: { strongest: strong.playerNumber, weakest: weak.playerNumber },
        });
        return options.length
            ? `${revealer.name} chooses what ${strong.name} takes from ${weak.name}.`
            : `${weak.name} has less than 3 of everything: no effect.`;
    },
    [EventNames.RavagesOfTime]: (gameState) => {
        forEachPlayer(gameState, player => {
            const options = player.wonders.filter(wonder => !wonder.crumbled && Level(wonder) <= 1)
                .map(wonder => ({ label: wonder.name, value: wonder.id }));
            const crumbled = RequestDecision(gameState, {
                player, type: 'crumble', event: EventNames.RavagesOfTime,
                prompt: "Choose one of your Age A or Age I wonders to crumble (it then produces only 2 culture)",
                options,
            });
            // With only one wonder to choose it crumbles at once: told as their choice, as if they had made it
            if (crumbled)
            {
                gameState.eventChoices?.push({ player: player.name, type: 'crumble', text: options[0].label });
            }
        });
        return "Each civilization turns one of its Age A or I wonders face down; it produces only 2 culture.";
    },
    [EventNames.Terrorism]: (gameState) => {
        const [leastCulture] = ranked(gameState, player => player.culture, false, CultureNote);
        // One building of each other civilization: other boards change, so the turn waits
        const targets = inTurnOrder(gameState).filter(player => player !== leastCulture);
        targets.forEach(target => RequestDecision(gameState, {
            player: leastCulture, type: 'destroy', event: EventNames.Terrorism, blocking: true, ui: 'boardPick',
            prompt: `Destroy one of ${target.name}'s urban buildings: pick it on their board`,
            options: builtCards(target, UrbanTypes).map(tech => ({ label: tech.name, value: tech.name })),
            context: { target: target.playerNumber, pickLabel: 'Destroy' },
        }));
        return `${leastCulture.name}, with the least culture, destroys an urban building of each other civilization.`;
    },
    [EventNames.Iconoclasm]: (gameState) => {
        const removed = inTurnOrder(gameState).filter(player => player.leader && player.leader.age !== gameState.age);
        removed.forEach(player => {
            FireTriggers(player, TriggerEvents.LeaderLeaves, { leader: player.leader });
            player.leader = null;
        });
        return removed.length
            ? `${names(removed)} remove${removed.length === 1 ? 's' : ''} a leader not from the current age.`
            : "Every leader in play is from the current age: no effect.";
    },
    [EventNames.NationalPride]: (gameState) => {
        const [most] = ranked(gameState, player => player.culture, true, CultureNote);
        most.culture += 5;
        return `${most.name}, with the most culture, scores 5 culture.`;
    },
    [EventNames.PopularizationOfScience]: (gameState) =>
        eachScores(gameState, player => player.GetScienceProduction(), "Each civilization scores culture equal to the science it produces."),
    [EventNames.Prosperity]: (gameState) => {
        forEachPlayer(gameState, player => player.GainFood(Math.min(8, player.GetHappyFaces())));
        return "Each civilization gains 1 food per happy face.";
    },
    [EventNames.Refugees]: (gameState) => {
        const [strong] = strongest(gameState);
        const [weak] = weakest(gameState);
        strong.culture += 3;
        gainPopulation(strong);
        loseCulture(weak, 3);
        losePopulation(gameState, weak, EventNames.Refugees);
        return `${strong.name} scores 3 culture and gains 1 population; ${weak.name} loses 3 culture and 1 population.`;
    },

    // ---------------- Age III impacts
    [EventNames.ImpactOfAgriculture]: (gameState) => eachScores(gameState,
        player => player.GetFoodProduction() + (player.GetFoodProduction() > GetConsumption(player.yellowBank) ? 4 : 0),
        "Each civilization scores the food it produces, plus 4 if that exceeds its consumption."),
    [EventNames.ImpactOfArchitecture]: (gameState) => eachScores(gameState, player => levelsOf(player, UrbanTypes),
        "Each civilization scores 1 culture per level of each urban building."),
    [EventNames.ImpactOfBalance]: (gameState) => eachScores(gameState,
        player => 2 * Math.min(player.GetScienceProduction(), player.GetCultureProduction(), player.GetFoodProduction(), player.GetResourceProduction()),
        "Each civilization scores double what it produces least of."),
    [EventNames.ImpactOfColonies]: (gameState) => eachScores(gameState, player => 3 * player.colonies.length,
        "Each civilization scores 3 culture per colony."),
    [EventNames.ImpactOfCompetition]: (gameState) => eachScores(gameState,
        player => levelsOf(player, [...MilitaryTypes, TechnologyTypes.Urban.Arena]),
        "Each civilization scores 1 culture per level of each military unit and arena."),
    [EventNames.ImpactOfCulture]: (gameState) => scoreByRank(gameState, player => player.GetCultureProduction(), "culture production"),
    [EventNames.ImpactOfGovernment]: (gameState) => eachScores(gameState,
        player => 2 * player.GetCivilActionCount() + player.GetMilitaryActionCount(),
        "Each civilization scores 2 culture per civil action and 1 per military action."),
    [EventNames.ImpactOfHappiness]: (gameState) => {
        forEachPlayer(gameState, player => {
            player.culture += Math.min(16, 2 * player.GetHappyFaces());
            loseCulture(player, 2 * player.GetDiscontentWorkers());
        });
        return "Each civilization scores 2 culture per happy face (up to 16) and loses 2 per discontent worker.";
    },
    [EventNames.ImpactOfHarmony]: (gameState) => eachScores(gameState,
        player => 4 * Math.min(workersOn(player, [Farm, Mine]), workersOn(player, UrbanTypes), workersOn(player, MilitaryTypes)),
        "Each civilization scores 4 culture for each farm and mine, urban building or military unit, whichever it has fewest of."),
    [EventNames.ImpactOfIndustry]: (gameState) => eachScores(gameState,
        player => ProducedBy(player.resourceProductionCalculationDelegate, [Mine]),
        "Each civilization scores the resources its mines produce."),
    [EventNames.ImpactOfPopulation]: (gameState) => eachScores(gameState,
        player => 2 * Math.max(0, allWorkers(player) - player.GetDiscontentWorkers() - 10),
        "Each civilization scores 2 culture per content worker above 10."),
    [EventNames.ImpactOfProgress]: (gameState) => eachScores(gameState,
        player => 2 * (Level(player.governmentTechnology) + player.technologies.filter(tech => SpecialTypes.includes(tech.type)).reduce((sum, tech) => sum + Level(tech), 0)),
        "Each civilization scores 2 culture per level of its government and special technologies."),
    [EventNames.ImpactOfScience]: (gameState) => scoreByRank(gameState, player => player.GetScienceProduction(), "science production"),
    [EventNames.ImpactOfStrength]: (gameState) => scoreByRank(gameState, strength, "strength"),
    [EventNames.ImpactOfTechnology]: (gameState) => eachScores(gameState,
        player => 4 * [...player.technologies, player.governmentTechnology].filter(tech => Level(tech) === 3).length,
        "Each civilization scores 4 culture per level III technology."),
    [EventNames.ImpactOfVariety]: (gameState) => eachScores(gameState,
        player => 2 * ([...MilitaryTypes, ...UrbanTypes].filter(type => workersOn(player, [type]) > 0).length
            + player.technologies.filter(tech => SpecialTypes.includes(tech.type)).length),
        "Each civilization scores 2 culture per type of unit, urban building and special technology it has."),
    [EventNames.ImpactOfWonders]: (gameState) => eachScores(gameState,
        player => player.wonders.reduce((sum, wonder) => sum + 5 - Level(wonder), 0),
        "Each civilization scores culture per wonder: 5 for Age A, 4 for Age I, 3 for Age II, 2 for Age III."),
}

// Resolves a revealed event or territory card and describes what it did
const ResolveEvent = (gameState, card) => {
    gameState.blueCause = card.name;
    if (card.type === MilitaryCardTypes.Territory)
    {
        return require('./Colonization').StartAuction(gameState, card);
    }
    // Another military card prepared as an event (Confucius): everyone gains 1 science
    if (card.type !== MilitaryCardTypes.Event)
    {
        forEachPlayer(gameState, player => { player.science += 1; });
        return `${require('../Utilities/CardRefs').CardRef(card)} is not an event: each civilization gains 1 science.`;
    }
    const effect = EventEffects[card.name];
    gameState.eventRankings = [];
    // Choices made for players at once, with nothing to choose from (see RavagesOfTime)
    gameState.eventChoices = [];
    try {
        return effect ? effect(gameState) : "This event's effect is not implemented yet.";
    } finally {
        // (taken by the move that revealed it: see Moves PrepareEvent)
        gameState.lastRankings = MergedRankings(gameState.eventRankings);
        gameState.eventRankings = null;
        gameState.lastChoices = gameState.eventChoices;
        gameState.eventChoices = null;
    }
}

// Strongest and weakest by the same numbers (no card adding to them for the weakest) are one line: "Strongest / weakest"
const MergedRankings = (rankings) => {
    const strongestOne = rankings.find(ranking => ranking.measure === 'strongest');
    const weakestOne = rankings.find(ranking => ranking.measure === 'weakest');
    if (!strongestOne || !weakestOne || weakestOne.entries.some(entry => entry.extras))
    {
        return rankings;
    }
    return rankings.filter(ranking => ranking !== weakestOne)
        .map(ranking => ranking === strongestOne ? { ...ranking, measure: 'strength', label: 'Strongest / weakest' } : ranking);
};

// What cards add only for a ranking, as text: "+3 Nostradamus"
const ExtrasText = (extras) => extras.map(extra => `${extra.value >= 0 ? '+' : ''}${extra.value} ${extra.source}`).join(', ');

// A ranking as text, for the journal: "Weakest: Matt 2, Andrew 3, Dave 4 (+3 Nostradamus)"
const RankingText = (ranking) => `${ranking.label ?? ranking.measure}: ${ranking.entries.map(entry =>
    `${entry.player} ${entry.value}${entry.extras ? ` (${ExtrasText(entry.extras)})` : ''}`).join(', ')}`;

module.exports = {
    ResolveEvent,
    RankingText,
    ExtrasText,
    CivilizationOptions: (gameState, player) => civilizationOptions(gameState, player),
    // The weakest civilizations (ties: the later in turn order is the weaker), at most one fewer than are playing, by
    // their strength as shown (not for an event: e.g. Boudica)
    Weakest: (gameState, count) => ranked(gameState, strength, false).slice(0, Math.min(count, inGame(gameState).length - 1)),
    EventEffects,
}
